'use strict';

/**
 * BlueprintRender
 * ---------------
 * The one place a blueprint sheet turns into HTML. Pure string building, no DOM
 * and no Pict dependency, so it runs both in the browser (PictView-Blueprint) and
 * on the server (the public /shared/:token page). Keeping a single render path is
 * what makes the in-app view and the shared public page look identical.
 *
 *   renderSheet(data, opts)        -> the inner sheet HTML (title block + plates)
 *   renderPublicDocument(data,opts)-> a full standalone HTML page (server use)
 *   SHEET_CSS                      -> the sheet styles (the view injects these too)
 *
 * opts:
 *   fIcon(itemType)  -> icon HTML; defaults to the bundled inline SVG set
 *   mediaBase        -> blob URL prefix ('/1.0/Media/' in-app; token-scoped public)
 *   links            -> 'app' (clickable #/... links) | 'inert' (plain cards)
 *   actionsHTML      -> markup dropped into the title block's action area
 *   loading          -> true to show the "drawing..." placeholder
 */

// ItemType -> icon name.
const ICON_FOR =
{
	Vision: 'Eye', Module: 'Box', Feature: 'Star', WorkItem: 'ListChecks',
	Media: 'Image', Documentation: 'FileText', Foundational: 'Book',
	Whiteboard: 'Network', Session: 'Robot', Comment: 'Message',
	Issue: 'Warning', TestRun: 'Flask', Feedback: 'Message', Prompt: 'Terminal'
};

// Minimal inline SVGs (stroke = currentColor so the sheet palette colors them).
// Used server-side and as the in-app fallback; the app view passes the Pict icon
// provider instead so the rest of the app's icon set stays consistent.
const ICON_SVG =
{
	Eye: '<circle cx="12" cy="12" r="3"/><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/>',
	Box: '<path d="M21 8 12 3 3 8v8l9 5 9-5Z"/><path d="M3 8l9 5 9-5"/><path d="M12 13v8"/>',
	Star: '<path d="M12 3l2.6 5.6 6.1.8-4.5 4.2 1.2 6L12 17l-5.4 2.8 1.2-6L3.3 9.4l6.1-.8Z"/>',
	Network: '<rect x="9" y="3" width="6" height="6" rx="1"/><rect x="3" y="15" width="6" height="6" rx="1"/><rect x="15" y="15" width="6" height="6" rx="1"/><path d="M12 9v3M6 15v-1h12v1"/>',
	FileText: '<path d="M14 3H6v18h12V7Z"/><path d="M14 3v4h4M9 12h6M9 16h6"/>',
	Book: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"/>',
	Robot: '<rect x="4" y="8" width="16" height="11" rx="2"/><path d="M12 8V4M9 13h.01M15 13h.01"/>',
	Message: '<path d="M21 12a8 8 0 0 1-11.5 7.2L4 21l1.8-5.5A8 8 0 1 1 21 12Z"/>',
	Warning: '<path d="M12 3 2 20h20Z"/><path d="M12 10v4M12 17h.01"/>',
	Image: '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="m4 18 5-5 4 3 3-3 4 4"/>',
	Flask: '<path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3"/>',
	ListChecks: '<path d="M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2"/>'
};

function _esc(pStr)
{
	return String(pStr == null ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function _bytes(pN)
{
	let tmpN = Number(pN) || 0;
	if (tmpN < 1024) { return tmpN + ' B'; }
	if (tmpN < 1024 * 1024) { return (tmpN / 1024).toFixed(0) + ' KB'; }
	return (tmpN / (1024 * 1024)).toFixed(1) + ' MB';
}

// Inline emphasis on ALREADY-ESCAPED text: `code`, **bold**, *italic*. Safe because
// the text is escaped first, so these only wrap escaped content in our own tags.
// Underscore italics are deliberately unsupported so snake_case (IDAssignedUser,
// SortOrder) is never mangled.
function _inline(pEscaped)
{
	return pEscaped
		.replace(/`([^`]+)`/g, '<code>$1</code>')
		.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
		.replace(/\*([^*\s][^*]*?)\*/g, '<em>$1</em>');
}

// A small, dependency-free, safe Markdown subset -> HTML: ATX headings (# .. ######),
// blank-line-separated paragraphs (single newlines become <br>), and unordered (-, *, +)
// and ordered (1.) lists. Every text run is escaped before any tag is added, so this is
// injection-safe and runs identically in the browser and on the public server page.
function _md(pRaw)
{
	let tmpLines = String(pRaw == null ? '' : pRaw).replace(/\r\n?/g, '\n').split('\n');
	let tmpOut = [];
	let tmpPara = [];
	let tmpList = null; // { type: 'ul' | 'ol', items: [] }
	let fFlushPara = () => { if (tmpPara.length) { tmpOut.push('<p>' + _inline(tmpPara.join('<br>')) + '</p>'); tmpPara = []; } };
	let fFlushList = () => { if (tmpList) { tmpOut.push('<' + tmpList.type + '>' + tmpList.items.map((pI) => '<li>' + _inline(pI) + '</li>').join('') + '</' + tmpList.type + '>'); tmpList = null; } };
	for (let i = 0; i < tmpLines.length; i++)
	{
		let tmpLine = tmpLines[i];
		let tmpH = tmpLine.match(/^(#{1,6})\s+(.*)$/);
		let tmpUL = tmpLine.match(/^\s*[-*+]\s+(.*)$/);
		let tmpOL = tmpLine.match(/^\s*\d+\.\s+(.*)$/);
		if (tmpLine.trim() === '') { fFlushPara(); fFlushList(); }
		else if (tmpH) { fFlushPara(); fFlushList(); let tmpN = tmpH[1].length; tmpOut.push('<h' + tmpN + '>' + _inline(_esc(tmpH[2])) + '</h' + tmpN + '>'); }
		else if (tmpUL) { fFlushPara(); if (!tmpList || tmpList.type !== 'ul') { fFlushList(); tmpList = { type: 'ul', items: [] }; } tmpList.items.push(_esc(tmpUL[1])); }
		else if (tmpOL) { fFlushPara(); if (!tmpList || tmpList.type !== 'ol') { fFlushList(); tmpList = { type: 'ol', items: [] }; } tmpList.items.push(_esc(tmpOL[1])); }
		else { fFlushList(); tmpPara.push(_esc(tmpLine)); }
	}
	fFlushPara(); fFlushList();
	return tmpOut.join('');
}

function _defaultIcon(pType)
{
	let tmpSVG = ICON_SVG[ICON_FOR[pType] || 'FileText'] || ICON_SVG.FileText;
	return '<span class="pict-icon"><svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' + tmpSVG + '</svg></span>';
}

function _mediaHTML(pItem, pState)
{
	let tmpMime = String(pItem.Mime || '');
	let tmpURL = pState.mediaBase + pItem.IDItem + '/Blob';
	let tmpCap = '<figcaption>' + _esc(pItem.Title) + '</figcaption>';
	if (tmpMime.indexOf('image/') === 0)
	{
		return '<figure class="bp-media bp-media-img"><a href="' + tmpURL + '" target="_blank" rel="noopener"><img src="' + tmpURL + '" alt="' + _esc(pItem.Title) + '" loading="lazy"></a>' + tmpCap + '</figure>';
	}
	if (tmpMime.indexOf('video/') === 0)
	{
		return '<figure class="bp-media bp-media-video"><video controls preload="metadata" src="' + tmpURL + '"></video>' + tmpCap + '</figure>';
	}
	// Anything else: a download row.
	return '<a class="bp-artifact bp-artifact-row bp-artifact-link" href="' + tmpURL + '" download="' + _esc(pItem.FileName || pItem.Title) + '">'
		+ '<span class="bp-artifact-t">' + _esc(pItem.FileName || pItem.Title) + '</span>'
		+ '<span class="bp-artifact-sum">Download' + (pItem.SizeBytes ? (' (' + _bytes(pItem.SizeBytes) + ')') : '') + '</span></a>';
}

// The in-app route (or, for Sessions, the session viewer) an item links out to.
function _itemHref(pItem, pType, pState)
{
	if (pState.inert) { return ''; }
	if (pItem.Link) { return '#' + pItem.Link; }
	if (pType === 'Session') { return '#/session/' + pItem.IDItem; }
	return '';
}

// A caret that rotates when its <details> is open (see .bp-fold-chev CSS).
const _CHEVRON = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>';

// An item that carries full Content unfolds inline: a native <details> so several
// segments can be open at once and the whole thing works with no JavaScript (the
// public /shared page has no app bundle). Open by default = the "unfolded" view.
function _foldHTML(pItem, pType, pState)
{
	let tmpPin = pItem.Pinned ? '<span class="bp-pin" title="Pinned to this blueprint">PINNED</span>' : '';
	let tmpHref = _itemHref(pItem, pType, pState);
	let tmpOpenLink = tmpHref ? ('<a class="bp-artifact-open" href="' + _esc(tmpHref) + '" title="Open this item">Open</a>') : '';
	// The item flows under its set's title block: the name is a heading, the Content
	// renders as Markdown beneath it. No box and no repeated set icon -- a native
	// <details> only so it can still fold, and so it works with no JavaScript.
	return '<details class="bp-artifact bp-artifact-fold" open>'
		+ '<summary class="bp-artifact-head">'
			+ '<span class="bp-artifact-t">' + _esc(pItem.Title) + tmpPin + '</span>'
			+ tmpOpenLink
			+ '<span class="bp-fold-chev" aria-hidden="true">' + _CHEVRON + '</span>'
		+ '</summary>'
		+ '<div class="bp-artifact-content bp-md">' + _md(pItem.Content) + '</div>'
	+ '</details>';
}

function _itemHTML(pItem, pType, pState)
{
	if (pType === 'Media') { return _mediaHTML(pItem, pState); }
	// Anything with full content unfolds inline instead of showing a compact card.
	if (pItem.Content) { return _foldHTML(pItem, pType, pState); }

	// No content: a flowing row (title + short summary), no box and no repeated set icon.
	let tmpSummary = pItem.Summary ? ('<span class="bp-artifact-sum">' + _esc(pItem.Summary) + '</span>') : '';
	let tmpPin = pItem.Pinned ? '<span class="bp-pin" title="Pinned to this blueprint">PINNED</span>' : '';
	let tmpInner = '<span class="bp-artifact-t">' + _esc(pItem.Title) + tmpPin + '</span>' + tmpSummary;

	// Public sheets render inert rows: a logged-out reader cannot follow an
	// in-app route, so there is nothing to link to.
	if (pState.inert) { return '<div class="bp-artifact bp-artifact-row">' + tmpInner + '</div>'; }

	let tmpHref = _itemHref(pItem, pType, pState);
	if (tmpHref) { return '<a class="bp-artifact bp-artifact-row bp-artifact-link" href="' + _esc(tmpHref) + '">' + tmpInner + '</a>'; }
	return '<div class="bp-artifact bp-artifact-row">' + tmpInner + '</div>';
}

// One set = one opaque panel with a drafting title block at its top (eyebrow label,
// a rule, the count) and its items flowing beneath, separated by hairline rules --
// no box-in-a-box and no per-item icon repeated from the header.
function _plateHTML(pGroup, pState)
{
	let tmpItems = pGroup.Items || [];
	if (!tmpItems.length) { return ''; }
	let tmpMedia = (pGroup.Type === 'Media');
	let tmpBody = tmpItems.map((pItem) => _itemHTML(pItem, pGroup.Type, pState)).join('');
	return '<section class="bp-group' + (tmpMedia ? ' bp-group-media' : '') + '">'
		+ '<header class="bp-group-head">'
			+ '<span class="bp-group-label">' + _esc(pGroup.Label || pGroup.Type) + '</span>'
			+ '<span class="bp-group-rule"></span>'
			+ '<span class="bp-group-count">' + tmpItems.length + '</span>'
		+ '</header>'
		+ '<div class="bp-artifacts">' + tmpBody + '</div>'
	+ '</section>';
}

// The Unfold all / Fold all control. Inline handlers walk up to the sheet and flip
// every <details.bp-fold>, so this works in-app and on the public page with no
// bundled script. Shown only when the sheet actually has foldable content.
const _FOLD_ALL = "var s=this.closest('.bp-sheet');if(s){var d=s.querySelectorAll('details.bp-fold');for(var i=0;i<d.length;i++){d[i].open=true;}}";
const _FOLD_NONE = "var s=this.closest('.bp-sheet');if(s){var d=s.querySelectorAll('details.bp-fold');for(var i=0;i<d.length;i++){d[i].open=false;}}";
function _foldBarHTML()
{
	return '<div class="bp-foldbar">'
		+ '<span class="bp-foldbar-label">Attached content</span>'
		+ '<button type="button" class="bp-foldbtn" onclick="' + _FOLD_ALL + '">Unfold all</button>'
		+ '<button type="button" class="bp-foldbtn" onclick="' + _FOLD_NONE + '">Fold all</button>'
	+ '</div>';
}

// The inner sheet HTML (what goes inside .bp-root / #Blueprint-Sheet).
function renderSheet(pData, pOpts)
{
	pOpts = pOpts || {};
	if (pOpts.loading) { return '<div class="bp-empty">Drawing the blueprint...</div>'; }
	if (!pData) { return '<div class="bp-empty">This blueprint could not be loaded.</div>'; }

	let tmpState =
	{
		fIcon: pOpts.fIcon || _defaultIcon,
		mediaBase: pOpts.mediaBase || '/1.0/Media/',
		inert: (pOpts.links === 'inert')
	};

	let tmpID = pData.IDWorkItem || 0;
	// An entity's own mark when the payload carries one (a module reads M-001, not WI-0); a work item's
	// payload carries none, so it keeps the exact WI- form this sheet has always drawn.
	// BlueprintNumber before the WI fallback: a blueprint addressed as ITSELF carries neither a
	// Designator nor a WorkItemNumber, and used to fall all the way through to the literal "WI #0" --
	// a first-class blueprint titling itself as work item zero. BlueprintAPI.normalize fills the
	// Designator for anything it reads, so this is the belt to that braces (the public share page and
	// the print renderer build payloads that never pass through the provider).
	let tmpNum = pData.Designator
		? pData.Designator
		: (pData.WorkItemNumber ? ('WI-' + String(pData.WorkItemNumber).padStart(3, '0'))
			: (pData.BlueprintNumber ? ('BP-' + String(pData.BlueprintNumber).padStart(3, '0')) : ('WI #' + tmpID)));
	let tmpStatus = pData.Status ? ('<span class="bp-status">' + _esc(pData.Status) + '</span>') : '';
	let tmpDesc = pData.Description ? ('<div class="bp-desc bp-md">' + _md(pData.Description) + '</div>') : '';
	let tmpActions = pOpts.actionsHTML || '';

	let tmpHTML = '<div class="bp-titleblock">'
		+ '<div class="bp-tb-main">'
			+ '<div class="bp-eyebrow">BLUEPRINT | ' + _esc(tmpNum) + '</div>'
			+ '<h1 class="bp-h1">' + _esc(pData.Title || ('Work item #' + tmpID)) + '</h1>'
			+ tmpDesc
		+ '</div>'
		+ '<div class="bp-tb-side">' + tmpStatus + tmpActions + '</div>'
	+ '</div>';

	let tmpGroups = pData.Groups || [];
	if (!tmpGroups.length) { tmpHTML += '<div class="bp-empty">Nothing is linked to this work item yet.</div>'; }
	// Show the Unfold/Fold control only when at least one linked item has content to unfold.
	let tmpHasFold = tmpGroups.some((pGroup) => (pGroup.Items || []).some((pItem) => !!pItem.Content));
	if (tmpHasFold) { tmpHTML += _foldBarHTML(); }
	tmpGroups.forEach((pGroup) => { tmpHTML += _plateHTML(pGroup, tmpState); });
	return '<div class="bp-sheet">' + tmpHTML + '</div>';
}

// Drafting-sheet styles. The in-app view injects these via its CSSMap; the public
// page inlines them. The .bp-root variables drive the whole palette.
const SHEET_CSS = /*css*/`
	.bp-root { --bp-sheet: #16294b; --bp-panel: #101f39; --bp-ink: #eef4fc; --bp-eye: #88a8d6; --bp-frame: rgba(208,226,247,0.42); --bp-cell: rgba(150,180,220,0.28); --bp-acc: #f0c44a;
		padding: 22px; max-width: 1100px; margin: 0 auto; }
	.bp-root .bp-empty { color: var(--bp-eye); padding: 60px 10px; text-align: center; font-size: 15px; }
	.bp-root .bp-sheet { background: var(--bp-sheet); border: 1.5px solid var(--bp-frame); border-radius: 12px; padding: 22px 24px 26px;
		background-image: linear-gradient(rgba(150,180,220,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(150,180,220,0.07) 1px, transparent 1px);
		background-size: 26px 26px; color: var(--bp-ink); }
	/* Readable text sits on an opaque panel so the graph-paper grid stays a surround, never a texture behind the words. */
	.bp-root .bp-titleblock { display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; background: var(--bp-panel); border: 1px solid var(--bp-cell); border-radius: 10px; padding: 20px 22px; margin-bottom: 16px; flex-wrap: wrap; }
	.bp-root .bp-eyebrow { font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: 2.5px; color: var(--bp-eye); text-transform: uppercase; }
	.bp-root .bp-h1 { font-size: 26px; font-weight: 600; margin: 5px 0 0; line-height: 1.2; overflow-wrap: anywhere; }
	.bp-root .bp-desc { color: #cfe0f6; font-size: 14.5px; line-height: 1.6; margin: 10px 0 0; max-width: 70ch; }
	.bp-root .bp-tb-side { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; flex-wrap: wrap; justify-content: flex-end; }
	/* Badge and action buttons share one height so the title-block row lines up. */
	.bp-root .bp-status { display: inline-flex; align-items: center; height: 30px; box-sizing: border-box; font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--bp-acc); border: 1px solid var(--bp-acc); border-radius: 6px; padding: 0 11px; }
	.bp-root .bp-act { display: inline-flex; align-items: center; height: 30px; box-sizing: border-box; font: inherit; font-size: 13px; padding: 0 14px; border-radius: 7px; border: 1.5px solid var(--bp-acc); background: transparent; color: var(--bp-acc); cursor: pointer; text-decoration: none; white-space: nowrap; }
	.bp-root .bp-act:hover { background: rgba(240,196,74,0.14); }
	.bp-root .bp-act-ghost { border-color: var(--bp-cell); color: var(--bp-ink); }
	.bp-root .bp-act-ghost:hover { border-color: var(--bp-frame); background: rgba(150,180,220,0.12); }
	/* One set = one opaque panel with a title-block header; items flow inside it. */
	.bp-root .bp-group { border: 1px solid var(--bp-cell); border-radius: 10px; padding: 15px 20px 6px; margin-bottom: 14px; background: var(--bp-panel); }
	.bp-root .bp-group-head { display: flex; align-items: center; gap: 12px; margin-bottom: 2px; }
	.bp-root .bp-group-label { font-family: ui-monospace, Menlo, monospace; font-size: 12px; letter-spacing: 2px; text-transform: uppercase; color: var(--bp-eye); white-space: nowrap; }
	.bp-root .bp-group-rule { flex: 1 1 auto; height: 1px; background: var(--bp-cell); }
	.bp-root .bp-group-count { font-family: ui-monospace, Menlo, monospace; font-size: 12px; color: var(--bp-ink); }
	.bp-root .bp-artifacts { display: block; }
	.bp-root .bp-group-media .bp-artifacts { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; margin-top: 12px; }
	/* Items flow, separated by hairline rules -- no box around each one, no repeated icon. */
	.bp-root .bp-artifact { display: block; padding: 12px 0; border-top: 1px solid var(--bp-cell); }
	.bp-root .bp-group-media .bp-artifact { border-top: 0; padding: 0; }
	.bp-root .bp-artifact-row { display: flex; align-items: baseline; gap: 10px; text-decoration: none; color: var(--bp-ink); }
	.bp-root .bp-artifact-t { flex: 1 1 auto; min-width: 0; font-size: 15px; font-weight: 600; color: var(--bp-ink); overflow-wrap: anywhere; }
	.bp-root .bp-artifact-row .bp-artifact-t { flex: 0 0 auto; }
	.bp-root .bp-artifact-sum { flex: 1 1 auto; font-size: 12.5px; color: #bcd0ec; line-height: 1.5; }
	.bp-root .bp-artifact-link:hover .bp-artifact-t { color: var(--bp-acc); }
	.bp-root .bp-pin { font-family: ui-monospace, Menlo, monospace; font-size: 9px; letter-spacing: 1px; color: var(--bp-acc); border: 1px solid var(--bp-acc); border-radius: 4px; padding: 1px 4px; margin-left: 7px; vertical-align: middle; }
	.bp-root .bp-media { margin: 0; border: 1px solid var(--bp-cell); border-radius: 8px; overflow: hidden; background: rgba(8,16,30,0.5); }
	.bp-root .bp-media img, .bp-root .bp-media video { display: block; width: 100%; height: 180px; object-fit: cover; background: #0b1525; }
	.bp-root .bp-media-video video { object-fit: contain; }
	.bp-root .bp-media figcaption { font-size: 11.5px; color: #bcd0ec; padding: 7px 10px; overflow-wrap: anywhere; }
	.bp-root .pict-icon { display: inline-flex; align-items: center; justify-content: center; }
	/* Unfolded view: the Unfold all / Fold all bar + the per-item accordions. */
	.bp-root .bp-foldbar { display: flex; align-items: center; gap: 10px; margin: 0 0 16px; }
	.bp-root .bp-foldbar-label { font-family: ui-monospace, Menlo, monospace; font-size: 11px; letter-spacing: 1.6px; text-transform: uppercase; color: var(--bp-eye); }
	.bp-root .bp-foldbtn { margin-left: 0; font: inherit; font-size: 12px; padding: 4px 11px; border-radius: 6px; border: 1px solid var(--bp-cell); background: transparent; color: var(--bp-ink); cursor: pointer; }
	.bp-root .bp-foldbtn:first-of-type { margin-left: auto; }
	.bp-root .bp-foldbtn:hover { border-color: var(--bp-acc); color: var(--bp-acc); }
	/* A content item folds in place; its head is the item name, the body flows beneath. Flat, no box. */
	.bp-root .bp-artifact-head { display: flex; align-items: baseline; gap: 10px; cursor: pointer; list-style: none; user-select: none; }
	.bp-root .bp-artifact-head::-webkit-details-marker { display: none; }
	.bp-root .bp-artifact-open { flex: 0 0 auto; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: var(--bp-eye); text-decoration: none; border: 1px solid var(--bp-cell); border-radius: 5px; padding: 2px 8px; }
	.bp-root .bp-artifact-open:hover { border-color: var(--bp-acc); color: var(--bp-acc); }
	.bp-root .bp-fold-chev { flex: 0 0 auto; color: var(--bp-eye); display: inline-flex; transition: transform 0.15s ease; }
	.bp-root .bp-artifact-fold[open] .bp-fold-chev { transform: rotate(180deg); }
	.bp-root .bp-artifact-content { margin-top: 8px; font-size: 13.5px; line-height: 1.62; color: #d6e4f7; overflow-wrap: anywhere; }
	/* Rendered Markdown (title-block description + unfolded content). */
	.bp-root .bp-md > :first-child { margin-top: 0; }
	.bp-root .bp-md > :last-child { margin-bottom: 0; }
	.bp-root .bp-md p { margin: 0 0 10px; }
	.bp-root .bp-md h1, .bp-root .bp-md h2, .bp-root .bp-md h3, .bp-root .bp-md h4, .bp-root .bp-md h5, .bp-root .bp-md h6 { font-family: ui-monospace, Menlo, monospace; font-weight: 600; letter-spacing: 0.4px; color: var(--bp-ink); line-height: 1.25; margin: 16px 0 8px; }
	.bp-root .bp-md h1 { font-size: 18px; } .bp-root .bp-md h2 { font-size: 16px; } .bp-root .bp-md h3 { font-size: 14px; }
	.bp-root .bp-md h4, .bp-root .bp-md h5, .bp-root .bp-md h6 { font-size: 13px; color: var(--bp-eye); }
	.bp-root .bp-md ul, .bp-root .bp-md ol { margin: 8px 0 10px; padding-left: 22px; }
	.bp-root .bp-md li { margin: 3px 0; }
	.bp-root .bp-md code { font-family: ui-monospace, Menlo, monospace; font-size: 0.9em; background: rgba(150,180,220,0.16); padding: 1px 5px; border-radius: 4px; }
	.bp-root .bp-md strong { font-weight: 600; color: #ffffff; }
	.bp-root .bp-md em { font-style: italic; }
	/* Print: a sharp paper drafting sheet. Light ink, no grid or interactive chrome,
	   every fold forced open, page breaks that never split a title block or an item. */
	@media print {
		.bp-root { padding: 0; max-width: none; }
		.bp-root .bp-sheet { background: #ffffff; background-image: none; border: 1px solid #26364f; border-radius: 0; color: #16233b; padding: 6mm; }
		.bp-root .bp-titleblock, .bp-root .bp-group { background: #ffffff; border: 1px solid #c4cfe0; border-radius: 0; break-inside: avoid; }
		.bp-root .bp-eyebrow, .bp-root .bp-group-label, .bp-root .bp-group-count { color: #46597a; }
		.bp-root .bp-h1, .bp-root .bp-desc, .bp-root .bp-artifact-t, .bp-root .bp-artifact-content, .bp-root .bp-artifact-sum, .bp-root .bp-md { color: #16233b; }
		.bp-root .bp-md strong { color: #000000; }
		.bp-root .bp-md code { background: #eef1f6; color: #16233b; }
		.bp-root .bp-group-rule, .bp-root .bp-artifact { border-color: #d5dded; }
		.bp-root .bp-status { color: #7a5b00; border-color: #b98f00; }
		.bp-root .bp-media { border-color: #c4cfe0; }
		.bp-root .bp-act, .bp-root .bp-foldbar, .bp-root .bp-fold-chev, .bp-root .bp-artifact-open { display: none !important; }
		.bp-root .bp-artifact-content { display: block !important; }
		.bp-root .bp-group-head { break-after: avoid; }
		.bp-root .bp-artifact, .bp-root .bp-media, .bp-root .bp-md h1, .bp-root .bp-md h2, .bp-root .bp-md h3 { break-inside: avoid; }
		.bp-root .bp-md h1, .bp-root .bp-md h2, .bp-root .bp-md h3, .bp-root .bp-md h4, .bp-root .bp-md h5, .bp-root .bp-md h6 { color: #22314c; }
	}
`;

// A complete standalone HTML page for the public /shared/:token view. Inlines the
// sheet CSS, a thin Plansheet bar, and a Copy-link button. No app bundle, no auth.
function renderPublicDocument(pData, pOpts)
{
	pOpts = pOpts || {};
	let tmpTitle = (pData && pData.Title) ? pData.Title : 'Blueprint';
	let tmpCopy = '<button class="bp-act" onclick="navigator.clipboard&&navigator.clipboard.writeText(location.href);this.textContent=\'Link copied\';">Copy link</button>';
	// THE DOWNLOAD, WHEN THIS LINK ACTUALLY GRANTS IT. PrintEnabled is a real, separate grant on the
	// share (WI #384), and the page offered no way to use it: a reader could only reach the PDF by
	// knowing the /Print URL and typing it. A grant nobody can exercise is the same as no grant.
	let tmpPrint = pOpts.printHref
		? ('<a class="bp-act" href="' + _esc(pOpts.printHref) + '">Download the sheet set (PDF)</a>')
		: '';
	let tmpSheet = renderSheet(pData, { mediaBase: pOpts.mediaBase, links: 'inert', actionsHTML: tmpPrint + tmpCopy });
	let tmpPageCSS = `
		html,body{margin:0;background:#0c1830;color:#eef4fc;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}
		.bp-public-bar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 26px;border-bottom:1px solid rgba(150,180,220,0.18);}
		.bp-public-brand{font-family:ui-monospace,Menlo,monospace;letter-spacing:2px;font-size:12px;color:#88a8d6;text-transform:uppercase;}
		.bp-public-tag{font-size:11px;color:#6f8cb6;letter-spacing:1px;text-transform:uppercase;}
		.bp-public-foot{text-align:center;color:#5d79a4;font-size:12px;padding:22px 10px 34px;}
		.bp-root{padding-top:26px;padding-bottom:6px;}
		@media print { html,body{background:#ffffff;} .bp-public-bar,.bp-public-foot{display:none;} .bp-root{padding:0;} }
	`;
	return '<!doctype html><html lang="en"><head><meta charset="utf-8">'
		+ '<meta name="viewport" content="width=device-width, initial-scale=1">'
		+ '<meta name="robots" content="noindex">'
		+ '<title>' + _esc(tmpTitle) + ' - Blueprint</title>'
		+ '<style>' + tmpPageCSS + SHEET_CSS + '</style></head>'
		+ '<body><div class="bp-public-bar"><span class="bp-public-brand">Plansheet</span><span class="bp-public-tag">Shared blueprint - read only</span></div>'
		+ '<div class="bp-root">' + tmpSheet + '</div>'
		+ '<div class="bp-public-foot">A read-only blueprint shared from Plansheet.</div>'
		+ '</body></html>';
}

/**
 * Wrap a rendered SHEET SET in the public chrome, for a login-free reader.
 *
 * The shared page shows the sheet set itself now, not a summary of it. It used to render the
 * blueprint's item groups while the PDF compiled the same blueprint into a document -- so the PDF
 * carried diagrams, provenance and addenda the page had never heard of, and a reader who downloaded it
 * got something that surprised them. Same compile, two presentations.
 *
 * The sheet HTML is a complete document with its own @page rules, so the chrome is INJECTED into it
 * rather than wrapping it: re-parenting paged-media CSS is how a sheet stops being the size it says it
 * is. The bar and the footer are hidden in print, so what comes out of a browser's own print dialog is
 * the sheet set and nothing else.
 */
function publicSheetPage(pSheetHTML, pOpts)
{
	let tmpOpts = pOpts || {};
	// THE PAPER PICKER IS FOR THE READER, not the owner. The person holding a share link is the one who
	// knows whether they are reading this on a laptop or pinning it to a wall, and the print route has
	// always taken sheet and reading as query parameters -- there was simply no control, so every
	// recipient got Letter whether or not it suited them.
	//
	// One select over Sheet/Reading PAIRS rather than two controls, because not every pairing is
	// printable: offering a reading its paper refuses produces a 400 the reader cannot act on.
	let tmpLayouts = Array.isArray(tmpOpts.layouts) ? tmpOpts.layouts : [];
	let tmpPicker = '';
	if (tmpOpts.printHref && tmpLayouts.length > 1)
	{
		tmpPicker = '<select class="bp-pub-act bp-pub-size" onchange="'
			+ 'var a=document.getElementById(\'bp-pub-dl\');'
			+ 'a.href=a.dataset.base+\'?\'+this.value;'
			// The first request for a paper renders it, which is not instant. Saying so beats a reader
			// clicking a link that appears to do nothing for several seconds.
			+ 'a.textContent=\'Download PDF\';">'
			+ tmpLayouts.map((pLayout, pIndex) =>
				'<option value="sheet=' + encodeURIComponent(pLayout.Sheet)
					+ '&amp;reading=' + encodeURIComponent(pLayout.Reading) + '"'
					+ (pIndex === 0 ? ' selected' : '') + '>' + _esc(pLayout.Label) + '</option>').join('')
			+ '</select>';
	}
	let tmpFirst = tmpLayouts.length
		? ('?sheet=' + encodeURIComponent(tmpLayouts[0].Sheet) + '&reading=' + encodeURIComponent(tmpLayouts[0].Reading))
		: '';
	let tmpDownload = tmpOpts.printHref
		? (tmpPicker + '<a class="bp-pub-act" id="bp-pub-dl" data-base="' + _esc(tmpOpts.printHref) + '" href="'
			+ _esc(tmpOpts.printHref + tmpFirst) + '">Download PDF</a>')
		: '';
	let tmpBar = '<div class="bp-pub-bar">'
		+ '<span class="bp-pub-brand">Plansheet</span>'
		+ '<span class="bp-pub-actions">' + tmpDownload
		+ '<button class="bp-pub-act" onclick="navigator.clipboard&&navigator.clipboard.writeText(location.href);'
		+ 'this.textContent=\'Link copied\';">Copy link</button></span></div>';
	let tmpCSS = '<style>'
		+ '.bp-pub-bar{position:sticky;top:0;z-index:9;display:flex;align-items:center;justify-content:space-between;'
		+ 'gap:12px;padding:10px 18px;background:#0c1830;color:#eef4fc;font-family:-apple-system,BlinkMacSystemFont,'
		+ '"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}'
		+ '.bp-pub-brand{font-family:ui-monospace,Menlo,monospace;letter-spacing:2px;font-size:12px;color:#88a8d6;'
		+ 'text-transform:uppercase;}'
		+ '.bp-pub-actions{display:flex;gap:8px;}'
		+ '.bp-pub-act{font:inherit;font-size:12px;padding:6px 12px;border-radius:4px;border:1px solid rgba(150,180,220,0.4);'
		+ 'background:transparent;color:#cfe0f6;cursor:pointer;text-decoration:none;}'
		+ '.bp-pub-size{background:#0c1830;color:#cfe0f6;}'
		+ '@media print{.bp-pub-bar{display:none;}}'
		+ '</style>';
	let tmpHTML = String(pSheetHTML || '');
	// Injected rather than concatenated: the sheet document owns <html> and <head>, and its @page rules
	// have to stay where they are.
	let tmpHeadAt = tmpHTML.indexOf('</head>');
	if (tmpHeadAt >= 0) { tmpHTML = tmpHTML.slice(0, tmpHeadAt) + tmpCSS + tmpHTML.slice(tmpHeadAt); }
	let tmpBodyAt = tmpHTML.indexOf('<body');
	if (tmpBodyAt >= 0)
	{
		let tmpBodyEnd = tmpHTML.indexOf('>', tmpBodyAt);
		if (tmpBodyEnd >= 0) { tmpHTML = tmpHTML.slice(0, tmpBodyEnd + 1) + tmpBar + tmpHTML.slice(tmpBodyEnd + 1); }
	}
	return tmpHTML;
}

/**
 * A public, read-only web page for a DESIGNED layout (BlueprintLayout), as opposed to the compiled plate
 * set. It is the SAME renderSheet the studio and the PDF use -- so the shared page, the in-app page and
 * the print cannot disagree -- sized to read as a page on screen and wrapped in the public bar. The
 * drawing style rides on the layout (layout.Kit).
 *
 * pOpts.printHref  the public print route; when set, the bar offers a per-size Download PDF.
 * pOpts.title      the browser tab title.
 */
function renderPublicLayout(pLayout, pOpts)
{
	let tmpLibLayout = require('./BlueprintLayout.js');
	let tmpOpts = pOpts || {};
	let tmpKit = (pLayout && pLayout.Kit) || tmpOpts.kit || 'clean';
	let tmpGrid = (pLayout && pLayout.Grid) || { Cols: 12, Rows: 16 };
	// A responsive layout drops the fixed grid-aspect pin and takes a viewport-sized box, so its aspect follows
	// the monitor (landscape on a wide screen, portrait on a narrow one) and the @container query inside the
	// sheet switches the arrangement. .bpl is container-type:size and will not take height from its content, so
	// the box must carry a resolved height. A non-responsive layout keeps the exact fixed-aspect box as before.
	let tmpResponsive = (pLayout && pLayout.Responsive === true);
	let tmpBoxCSS = tmpResponsive
		? '.bp-pub-layout{width:min(100%,1180px);height:min(82vh,860px);box-shadow:0 14px 52px rgba(0,0,0,.36);}'
		: '.bp-pub-layout{width:100%;max-width:860px;aspect-ratio:' + tmpGrid.Cols + '/' + tmpGrid.Rows + ';box-shadow:0 14px 52px rgba(0,0,0,.36);}';
	// In print the sheet is WIDTH-DRIVEN: it fills the printable width and aspect-ratio derives a height a little
	// shorter than the page, so it never overflows onto a second sheet (Safari does not honour @page{margin:0} and
	// resolves vh against the screen, so neither a page-height nor 100vh is safe). A responsive layout prints at the
	// portrait sheet aspect; a fixed-aspect layout keeps its own grid proportions.
	let tmpPrintBox = tmpResponsive
		? '.bp-pub-layout{width:100% !important;height:auto !important;aspect-ratio:85 / 110;max-width:6.25in !important;margin:0 auto !important;box-shadow:none !important;}'
		: '.bp-pub-layout{width:100% !important;height:auto !important;aspect-ratio:' + tmpGrid.Cols + ' / ' + tmpGrid.Rows + ';max-width:6.25in !important;margin:0 auto !important;box-shadow:none !important;}';
	let tmpCSS = '<style>' + (tmpOpts.fontCSS || '') + tmpLibLayout.SHEET_CSS
		+ 'html,body{margin:0;background:#2a3344;}'
		+ '.bp-pub-stage{display:flex;justify-content:center;padding:28px 16px 64px;}'
		+ tmpBoxCSS
		// Width-driven so the sheet fits the real printable area in any engine; see renderPublicDeck for why a page
		// height (vh or inches) splits in Safari. The stage fills the printable width and the box derives its height.
		+ '@media print{@page{margin:0.25in;}html,body{background:#fff !important;margin:0;}'
		+ '.bp-pub-stage{width:100%;box-sizing:border-box;padding:0;display:flex;justify-content:center;align-items:flex-start;overflow:hidden;}' + tmpPrintBox + '}'
		+ '</style>';
	// Non-responsive keeps the exact fit script as before (run on parse + on fonts.ready). A responsive page
	// also re-fits on resize via a rAF-debounced ResizeObserver, because crossing 1:1 re-cells every text region.
	let tmpFit;
	if (tmpResponsive)
	{
		let tmpSrc = tmpLibLayout.fitLayout.toString();
		tmpFit = 'var fitLayout=' + tmpSrc + ';fitLayout(document);'
			+ 'if(document.fonts&&document.fonts.ready){document.fonts.ready.then(function(){fitLayout(document);});}'
			+ 'window.addEventListener("beforeprint",function(){fitLayout(document);});'
			+ 'var _b=document.querySelector(".bp-pub-layout");if(_b&&typeof ResizeObserver==="function"){new ResizeObserver(function(){fitLayout(document);}).observe(_b);}';
	}
	else
	{
		tmpFit = '(' + tmpLibLayout.fitLayout.toString() + ')(document);'
			+ 'if(document.fonts&&document.fonts.ready){document.fonts.ready.then(function(){(' + tmpLibLayout.fitLayout.toString() + ')(document);});}'
			+ 'window.addEventListener("beforeprint",function(){(' + tmpLibLayout.fitLayout.toString() + ')(document);});';
	}
	let tmpPage = '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">'
		+ '<title>' + _esc(tmpOpts.title || 'Blueprint') + '</title>' + tmpCSS + '</head><body>'
		+ '<div class="bp-pub-stage"><div class="bp-pub-layout">' + tmpLibLayout.renderSheet(pLayout, { Kit: tmpKit, Brand: tmpOpts.brand }) + '</div></div>'
		+ '<script>' + tmpFit + '</script></body></html>';
	return publicSheetPage(tmpPage, { printHref: tmpOpts.printHref, layouts: tmpOpts.layouts });
}

/**
 * A public, read-only DECK: several designed layouts presented as flip-through pages on one page. A set
 * blueprint (its layout carries an ordered Pages list of member blueprints) shares as this. Each page is the
 * SAME renderSheet every other surface uses, in its own viewport-sized responsive box, so a page reads
 * side-by-side on a wide monitor and stacked on a narrow one, exactly as it does on its own share link.
 *
 * Navigation is native horizontal scroll-snap (swipe / trackpad), plus prev/next buttons, page dots, a
 * counter and the arrow keys. Printing lays the pages out one per sheet.
 *
 * pPages  [{ Layout (object or JSON string), Name }]
 * pOpts.title  the browser tab title.
 */
function renderPublicDeck(pPages, pOpts)
{
	let tmpLibLayout = require('./BlueprintLayout.js');
	let tmpOpts = pOpts || {};
	let tmpPages = (Array.isArray(pPages) ? pPages : []).map((pPage) =>
	{
		let tmpLayout = pPage.Layout;
		if (typeof tmpLayout === 'string') { try { tmpLayout = JSON.parse(tmpLayout); } catch (pErr) { tmpLayout = null; } }
		return tmpLayout ? { Layout: tmpLayout, Name: pPage.Name || '' } : null;
	}).filter(Boolean);

	let tmpSlides = tmpPages.map((pPage, pIndex) =>
		'<section class="bp-deck-slide" data-i="' + pIndex + '">'
			+ '<div class="bp-deck-box">' + tmpLibLayout.renderSheet(pPage.Layout,
				{ Kit: pPage.Layout.Kit || 'clean', Page: { Index: pIndex + 1, Total: tmpPages.length }, Brand: tmpOpts.brand }) + '</div></section>').join('');

	let tmpDots = tmpPages.map((pPage, pIndex) =>
		'<button class="bp-deck-dot' + (pIndex === 0 ? ' is-on' : '') + '" data-i="' + pIndex + '" title="' + _esc(pPage.Name || ('Page ' + (pIndex + 1))) + '"></button>').join('');

	let fChevron = (pDir) => '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="'
		+ (pDir === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6') + '"></polyline></svg>';

	let tmpCSS = '<style>' + (tmpOpts.fontCSS || '') + tmpLibLayout.SHEET_CSS
		+ 'html,body{margin:0;height:100%;background:#0c1830;color:#eef4fc;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;}'
		+ '.bp-deck-bar{position:sticky;top:0;z-index:9;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 18px;background:#0c1830;border-bottom:1px solid rgba(150,180,220,0.18);}'
		+ '.bp-deck-brand{font-family:ui-monospace,Menlo,monospace;letter-spacing:2px;font-size:12px;color:#88a8d6;text-transform:uppercase;}'
		+ '.bp-deck-mid{font-family:ui-monospace,Menlo,monospace;font-size:12px;color:#cfe0f6;letter-spacing:1px;}'
		+ '.bp-deck-acts{display:flex;align-items:center;gap:8px;}'
		+ '.bp-deck-act{font:inherit;font-size:12px;padding:6px 12px;border-radius:4px;border:1px solid rgba(150,180,220,0.4);background:transparent;color:#cfe0f6;cursor:pointer;text-decoration:none;}'
		+ '.bp-deck{display:flex;overflow-x:auto;overflow-y:hidden;scroll-snap-type:x mandatory;scroll-behavior:smooth;height:calc(100vh - 45px);scrollbar-width:none;}'
		+ '.bp-deck::-webkit-scrollbar{display:none;}'
		+ '.bp-deck-slide{flex:0 0 100%;scroll-snap-align:center;display:flex;align-items:center;justify-content:center;box-sizing:border-box;padding:18px 16px 52px;}'
		+ '.bp-deck-box{width:min(100%,1180px);height:min(84vh,880px);box-shadow:0 14px 52px rgba(0,0,0,.36);}'
		+ '.bp-deck-nav{position:fixed;top:calc(50% + 22px);transform:translateY(-50%);z-index:10;width:42px;height:42px;border-radius:50%;border:1px solid rgba(150,180,220,0.4);background:rgba(12,24,48,0.82);color:#cfe0f6;display:flex;align-items:center;justify-content:center;cursor:pointer;}'
		+ '.bp-deck-nav:disabled{opacity:0.28;cursor:default;}'
		+ '.bp-deck-prev{left:14px;}.bp-deck-next{right:14px;}'
		+ '.bp-deck-dots{position:fixed;bottom:15px;left:0;right:0;z-index:10;display:flex;gap:8px;justify-content:center;}'
		+ '.bp-deck-dot{width:8px;height:8px;border-radius:50%;border:0;background:rgba(150,180,220,0.35);cursor:pointer;padding:0;}'
		+ '.bp-deck-dot.is-on{background:#8fd3ff;}'
		// The printed sheet is sized CONSERVATIVELY to clear Safari's print furniture, not to fill every inch.
		// Browser print cannot know the real printable area: Safari ignores @page{margin:0} (it clamps to the
		// printer's forced margins) and reserves more space again when "Print headers and footers" is on, so a
		// sheet sized to the full page -- whether by 100vh, an explicit height, or 100% of the printable width --
		// overflowed that furniture and spilled its bottom strip onto a second sheet. Instead the box is capped at
		// 6.25in wide and aspect-ratio derives a ~8.1in height, which fits inside Letter and A4 even with generous
		// forced margins AND headers/footers on, so one blueprint is always one sheet in every engine. The box is
		// deliberately well short of the page: a tighter fit tips the title-block strip onto a second sheet on a
		// printer that reserves more furniture. The Download PDF (server-rendered)
		// is the full-bleed output; this is the quick browser path. break-after keeps one per page; overflow clips.
		+ '@media print{@page{margin:0.25in;}html,body{background:#fff !important;margin:0;}'
		+ '.bp-deck-bar,.bp-deck-nav,.bp-deck-dots{display:none;}'
		+ '.bp-deck{display:block;overflow:visible;height:auto;}'
		+ '.bp-deck-slide{width:100%;box-sizing:border-box;padding:0;display:flex;justify-content:center;align-items:flex-start;overflow:hidden;break-inside:avoid;}'
		+ '.bp-deck-slide:not(:last-child){break-after:page;}'
		+ '.bp-deck-box{width:100% !important;height:auto !important;aspect-ratio:85 / 110;max-width:6.25in !important;margin:0 auto !important;box-shadow:none !important;}}'
		+ '</style>';

	let tmpScript = 'var fitLayout=' + tmpLibLayout.fitLayout.toString() + ';'
		+ 'function fitAll(){fitLayout(document);}fitAll();'
		+ 'if(document.fonts&&document.fonts.ready){document.fonts.ready.then(fitAll);}'
		// Re-fit when print is invoked: print re-sizes each sheet to the full page, which re-cells every text
		// region, so the on-screen fit is stale. beforeprint fires after print media applies, in time for the
		// snapshot (and covers the headless print-to-PDF path).
		+ 'window.addEventListener("beforeprint",fitAll);'
		+ 'var deck=document.getElementById("bp-deck"),dots=document.querySelectorAll(".bp-deck-dot");'
		+ 'var prev=document.getElementById("bp-deck-prev"),next=document.getElementById("bp-deck-next"),counter=document.getElementById("bp-deck-counter");'
		+ 'var n=deck.querySelectorAll(".bp-deck-slide").length,cur=0;'
		+ 'function go(i){cur=Math.max(0,Math.min(n-1,i));deck.scrollTo({left:cur*deck.clientWidth,behavior:"smooth"});sync();}'
		+ 'function sync(){cur=Math.round(deck.scrollLeft/Math.max(1,deck.clientWidth));for(var j=0;j<dots.length;j++){dots[j].classList.toggle("is-on",j===cur);}if(counter){counter.textContent=(cur+1)+" / "+n;}if(prev){prev.disabled=(cur<=0);}if(next){next.disabled=(cur>=n-1);}}'
		+ 'if(prev){prev.addEventListener("click",function(){go(cur-1);});}'
		+ 'if(next){next.addEventListener("click",function(){go(cur+1);});}'
		+ 'for(var k=0;k<dots.length;k++){(function(idx){dots[idx].addEventListener("click",function(){go(idx);});})(k);}'
		+ 'var st;deck.addEventListener("scroll",function(){clearTimeout(st);st=setTimeout(sync,90);});'
		+ 'document.addEventListener("keydown",function(e){if(e.key==="ArrowRight"){go(cur+1);}else if(e.key==="ArrowLeft"){go(cur-1);}});'
		// Re-fit SYNCHRONOUSLY on box resize (ResizeObserver fires after layout, before paint -- so this catches
		// the switch to print media and re-fits before the page is snapshotted, which a rAF-deferred fit misses in
		// headless print-to-PDF). fitLayout only scales text, never the box, so there is no observer loop. The
		// scroll-position restore stays rAF-debounced; it is a screen-only nicety.
		+ 'if(typeof ResizeObserver==="function"){var _rp=0;var ro=new ResizeObserver(function(){fitAll();if(_rp){return;}_rp=requestAnimationFrame(function(){_rp=0;deck.scrollLeft=cur*deck.clientWidth;});});document.querySelectorAll(".bp-deck-box").forEach(function(b){ro.observe(b);});}'
		+ 'sync();';

	// The whole deck prints server side (one headless render, one sheet per page), so the reader downloads a
	// PDF that reads whole in every browser rather than invoking their own Print, which in Firefox drops the
	// SVG line-work. The link is offered only when the share grants printing; without the grant the route
	// would 403 and a dead button is worse than none.
	let tmpDownload = tmpOpts.printHref
		? ('<a class="bp-deck-act" href="' + _esc(tmpOpts.printHref) + '">Download PDF</a>')
		: '';
	let tmpBar = '<div class="bp-deck-bar"><span class="bp-deck-brand">Plansheet</span>'
		+ '<span class="bp-deck-mid" id="bp-deck-counter">1 / ' + tmpPages.length + '</span>'
		+ '<span class="bp-deck-acts">' + tmpDownload
		+ '<button class="bp-deck-act" onclick="navigator.clipboard&&navigator.clipboard.writeText(location.href);this.textContent=\'Link copied\';">Copy link</button></span></div>';

	return '<!doctype html><html lang="en"><head><meta charset="utf-8">'
		+ '<meta name="viewport" content="width=device-width,initial-scale=1">'
		+ '<meta name="robots" content="noindex">'
		+ '<title>' + _esc(tmpOpts.title || 'Blueprint set') + '</title>' + tmpCSS + '</head><body>'
		+ tmpBar
		+ '<div class="bp-deck" id="bp-deck">' + tmpSlides + '</div>'
		+ '<button class="bp-deck-nav bp-deck-prev" id="bp-deck-prev" aria-label="Previous">' + fChevron('prev') + '</button>'
		+ '<button class="bp-deck-nav bp-deck-next" id="bp-deck-next" aria-label="Next">' + fChevron('next') + '</button>'
		+ '<div class="bp-deck-dots">' + tmpDots + '</div>'
		+ '<script>' + tmpScript + '</script></body></html>';
}

module.exports = { renderSheet, renderPublicDocument, publicSheetPage, renderPublicLayout, renderPublicDeck, SHEET_CSS, ICON_FOR, ICON_SVG };
